// Shared vocabulary for Broadcasts + Templates (Communications, Phase 3).
import { contactRoleLabel } from './outreach';

export const BROADCAST_STATUS_META = {
  draft:     { label: 'Draft',     tone: 'bg-gray-500/10 text-gray-300 border-gray-500/20' },
  sending:   { label: 'Sending',   tone: 'bg-amber-500/10 text-amber-300 border-amber-500/20' },
  sent:      { label: 'Sent',      tone: 'bg-emerald-500/10 text-emerald-300 border-emerald-500/20' },
  cancelled: { label: 'Cancelled', tone: 'bg-gray-500/10 text-gray-500 border-gray-500/20' },
};
export function broadcastStatusMeta(s) {
  return BROADCAST_STATUS_META[s] || { label: s || '—', tone: 'bg-gray-500/10 text-gray-400 border-gray-500/20' };
}

export const RECIPIENT_STATUS_META = {
  pending: { label: 'Waiting',  tone: 'text-gray-400' },
  sending: { label: 'Sending…', tone: 'text-amber-300' },
  sent:    { label: 'Sent',     tone: 'text-emerald-300' },
  failed:  { label: 'Failed',   tone: 'text-red-300' },
  skipped: { label: 'Skipped',  tone: 'text-gray-500' },
};

export const STAGES = [
  ['prospect', 'Prospect'], ['contacted', 'Contacted'], ['engaged', 'Engaged'], ['verbal_yes', 'Verbal yes'],
  ['contracted', 'Contracted'], ['maintained', 'Maintained'], ['dormant', 'Dormant'], ['closed_lost', 'Closed lost'],
];
export const STAGE_LABELS = Object.fromEntries(STAGES);
export function stageLabel(s) { return STAGE_LABELS[s] || String(s || '').replace(/_/g, ' '); }

// NYC DOE single-letter borough codes, as stored on accounts.
export const BOROUGHS = [
  ['M', 'Manhattan'], ['X', 'Bronx'], ['K', 'Brooklyn'], ['Q', 'Queens'], ['R', 'Staten Island'],
];
export const BOROUGH_LABELS = Object.fromEntries(BOROUGHS);
export function boroughLabel(code) { return BOROUGH_LABELS[code] || code || ''; }

export const PURPOSE_COPY = {
  service: {
    label: 'Service correspondence',
    detail: 'About work the school has engaged us for — check-ins, launches, maintenance notes. No marketing footer.',
  },
  commercial: {
    label: 'Commercial',
    detail: 'Offers or promotion. Adds our address and a one-click unsubscribe (CAN-SPAM), and skips schools that opted out.',
  },
};

export function skipReasonLabel(reason, roles = [], titles = []) {
  switch (reason) {
    case 'opted_out': return 'School opted out of NexSav email';
    case 'no_contact': {
      const who = [...(roles || []).map((r) => contactRoleLabel(r).toLowerCase()), ...(titles || []).map((t) => `“${t}”`)];
      return `No ${who.length ? who.join(' or ') : 'contact'} on file`;
    }
    case 'no_email': return 'Contact has no email address';
    case 'suppressed': return 'Address is on the suppression list (bounced or unsubscribed)';
    case 'cancelled': return 'Cancelled before it was sent';
    default: return reason || 'Skipped';
  }
}

// "Maintained + Contracted · Bronx · District 9 · 2 schools removed · Principal"
export function audienceSummary(aud) {
  if (!aud) return '';
  const parts = [];
  if (aud.stages?.length) parts.push(aud.stages.map(stageLabel).join(' + '));
  if (aud.borough) parts.push(boroughLabel(aud.borough));
  if (aud.district) parts.push(`District ${aud.district}`);
  if (aud.type) parts.push(aud.type === 'school' ? 'Schools' : aud.type === 'district' ? 'District offices' : 'External');
  if (aud.account_ids?.length) parts.push(`${aud.account_ids.length} hand-picked school${aud.account_ids.length === 1 ? '' : 's'}`);
  if (!parts.length) return 'No audience yet';
  // Removed schools narrow an audience; on their own they are not one.
  const removed = aud.exclude_account_ids?.length || 0;
  if (removed) parts.push(`${removed} school${removed === 1 ? '' : 's'} removed`);
  const roles = [...(aud.roles || []).map(contactRoleLabel), ...(aud.titles || []).map((t) => `“${t}”`)].join(', ');
  return `${parts.join(' · ')}${roles ? ` · ${roles}` : ''}${aud.combine ? ' · one email per school, everyone in To' : ''}`;
}

export function audienceHasCriteria(aud) {
  return !!(aud && (aud.stages?.length || aud.borough || aud.district || aud.type || aud.account_ids?.length));
}

export function hasTokens(s) {
  return /\{\{\s*[a-zA-Z_][a-zA-Z0-9_.]*\s*\}\}/.test(String(s || ''));
}
